import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Stack, Typography, Paper, Table, TableContainer, TableHead, TableBody,
  TableRow, TableCell, TextField, ToggleButton, ToggleButtonGroup, Chip, Tooltip,
  LinearProgress, Snackbar, Alert, Collapse, Button,
  InputAdornment, IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CodeIcon from '@mui/icons-material/Code';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import LogoutIcon from '@mui/icons-material/Logout';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import authService from '../services/auth';

const API = process.env.REACT_APP_API_URL;

// Binary by design. Reviewer 1 asks how often AI-inferred findings are
// hallucinations rather than valid inferences, which is a two-way question, so
// the classification answers it in the same terms.
const VERDICTS = [
  { key: 'sound', label: 'Sound inference', colour: 'success',
    help: 'The configuration supports this finding, even though no single line matched.' },
  { key: 'fabricated', label: 'Fabricated', colour: 'error',
    help: 'The configuration does not support this finding.' },
];
// Brightened for the dark theme (background #0a0e1a, paper #1e293b).
const COLOUR = { sound: '#66bb6a', fabricated: '#ef5350' };
const WASH_GREEN = 'rgba(102, 187, 106, 0.12)';

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('access_token')}` };
}

export default function ProvenanceClassification() {
  const [items, setItems] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(null);   // queue_id whose reasoning is open for editing
  const [draft, setDraft] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/provenance/queue`, { headers: authHeaders() });
        if (res.status === 401) { if (!cancelled) setError('Session expired — sign in again.'); return; }
        if (!res.ok) throw new Error(`Queue unavailable (HTTP ${res.status})`);
        const data = await res.json();
        if (cancelled) return;
        setItems(data.items || []);
        setAnswers(data.answers || {});
        setMe(data.meta || null);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = useCallback(async (itemId, next) => {
    setSavingId(itemId);
    try {
      const res = await fetch(`${API}/provenance/answer`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ item_id: itemId, verdict: next.verdict, rationale: next.rationale || '' }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.detail || `Save failed (HTTP ${res.status})`);
      }
    } catch (e) {
      setToast({ severity: 'error', msg: e.message });
    } finally {
      setSavingId(null);
    }
  }, []);

  const update = (itemId, patch) => {
    const next = { ...(answers[itemId] || {}), ...patch };
    setAnswers((p) => ({ ...p, [itemId]: next }));
    if (next.verdict) save(itemId, next);
  };

  // Reasoning is edited one item at a time. Without a verdict it is held locally
  // and goes to the server with the verdict, as before.
  const startEdit = (itemId) => {
    if (editing && editing !== itemId) update(editing, { rationale: draft.trim() });
    setEditing(itemId);
    setDraft(answers[itemId]?.rationale || '');
  };
  const cancelEdit = () => { setEditing(null); setDraft(''); };
  const commitEdit = (itemId) => {
    update(itemId, { rationale: draft.trim() });
    setEditing(null);
    setDraft('');
  };

  // --- the figures that go into the paper -------------------------------
  // Progress only. Deliberately no per-verdict counts: see the note above.
  const stats = useMemo(() => {
    const done = Object.values(answers).filter((a) => a?.verdict).length;
    return { done, total: items.length };
  }, [answers, items.length]);



  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      const a = answers[it.queue_id];
      if (statusFilter === 'todo' && a?.verdict) return false;
      if (statusFilter === 'done' && !a?.verdict) return false;
      if (q) {
        const hay = `${it.rule} ${it.issue} ${it.vendor} ${it.device_name}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [items, answers, statusFilter, search]);

  const downloadCsv = useCallback(() => {
    const cell = (v) => {
      const t = String(v ?? '');
      return /[",\r\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
    };
    // The tag and the line it cites are not columns here, matching what the
    // classification view shows.
    const header = ['#', 'Item', 'Audit', 'Device', 'Vendor', 'Model', 'Severity',
                    'Rule', 'Issue', 'Recommendation', 'Classification', 'Reasoning'];
    // The configuration is deliberately not a column: it is the same text
    // repeated across every finding for a device, and would bury the judgements.
    const body = filtered.map((it, n) => {
      const a = answers[it.queue_id] || {};
      const v = VERDICTS.find((x) => x.key === a.verdict);
      return [n + 1, it.queue_id, it.audit_history_id, it.device_name, it.vendor,
              it.ai_model, it.severity, it.rule, it.issue, it.recommendation,
              v ? v.label : '', a.rationale || ''];
    });
    const csv = [header, ...body].map((r) => r.map(cell).join(',')).join('\r\n');

    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `nscat-finding-classification-${body.length}rows.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Freeing it immediately can abort the download in some browsers.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setToast({ severity: 'success', msg: `Downloaded ${body.length} row${body.length === 1 ? '' : 's'} as CSV` });
  }, [filtered, answers]);

  if (loading) return <Box sx={{ p: 4 }}><LinearProgress /></Box>;
  if (error) return <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}><Alert severity="error">{error}</Alert></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 1600, mx: 'auto' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Finding Classification</Typography>
          <Typography variant="body2" color="text.secondary">
            One question for every finding: <b>does the configuration support it?</b> Open
            the device configuration and judge each finding against it.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {authService.getUsername?.() || ''}
          </Typography>
          <Tooltip arrow title="Sign out">
            <IconButton size="small" onClick={() => authService.logout()}>
              <LogoutIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* No running split is shown here. A classifier who can watch one verdict
          pull ahead is anchored by it; the figures live on the admin stats page. */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 160 }}>
            {stats.done} of {stats.total} classified
          </Typography>
          <LinearProgress
            variant="determinate"
            value={stats.total ? (100 * stats.done) / stats.total : 0}
            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
          />
          {stats.done === stats.total && stats.total > 0 && (
            <Chip size="small" color="success" icon={<CheckCircleIcon />} label="complete" />
          )}
        </Stack>
      </Paper>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small" placeholder="search rule, issue, vendor…" value={search}
            onChange={(e) => setSearch(e.target.value)} sx={{ width: 320 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <ToggleButtonGroup size="small" exclusive value={statusFilter}
            onChange={(_, v) => v && setStatusFilter(v)}>
            <ToggleButton value="all">All ({items.length})</ToggleButton>
            <ToggleButton value="todo">To do ({items.length - stats.done})</ToggleButton>
            <ToggleButton value="done">Classified ({stats.done})</ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            Showing {filtered.length} of {items.length}
          </Typography>
          <Tooltip
            arrow
            title={
              filtered.length === items.length
                ? `Download all ${items.length} rows as a CSV file`
                : `Download the ${filtered.length} row${filtered.length === 1 ? '' : 's'} matching the current filters, as CSV`
            }
          >
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={downloadCsv}
                disabled={!filtered.length}
                aria-label={`Download ${filtered.length} classifications as a CSV file`}
                sx={{ textTransform: 'none' }}
              >
                CSV
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 52 }}>#</TableCell>
              <TableCell sx={{ width: 96 }}>Severity</TableCell>
              <TableCell sx={{ width: 128 }}>Vendor</TableCell>
              <TableCell>Finding</TableCell>
              <TableCell sx={{ width: 300 }}>Is it supported by the configuration?</TableCell>
              <TableCell sx={{ width: 64 }} align="center">Config</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((it, n) => {
              const a = answers[it.queue_id] || {};
              const open = expanded === it.queue_id;
              return (
                <React.Fragment key={it.queue_id}>
                  <TableRow hover sx={{ '& td': { verticalAlign: 'top' } }}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{n + 1}</Typography>
                      {savingId === it.queue_id && (
                        <Typography variant="caption" color="text.secondary">saving…</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={it.severity} sx={{ height: 18, fontSize: '0.65rem' }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ display: 'block' }}>{it.vendor}</Typography>
                      <Typography sx={{ fontSize: '0.6rem', opacity: 0.7 }}>audit {it.audit_history_id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {it.rule}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                        {it.issue}
                      </Typography>
                      {/* Every item is presented the same way: the finding and one question. */}
                      <Typography sx={{ mt: 0.5, fontSize: '0.62rem', opacity: 0.75 }}>
                        Does the configuration support this finding?
                      </Typography>
                      {editing === it.queue_id ? (
                        <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.75 }}>
                          <TextField
                            size="small" fullWidth multiline minRows={2} maxRows={6} autoFocus
                            placeholder="reasoning (optional)"
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') cancelEdit();
                              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) commitEdit(it.queue_id);
                            }}
                            inputProps={{ style: { fontSize: '0.72rem' } }}
                          />
                          <Tooltip arrow title="Save reasoning (Ctrl+Enter)">
                            <IconButton size="small" color="primary" aria-label="Save reasoning"
                              onClick={() => commitEdit(it.queue_id)}>
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip arrow title="Cancel (Esc)">
                            <IconButton size="small" aria-label="Cancel editing" onClick={cancelEdit}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 0.75 }}>
                          <Typography
                            sx={{
                              flex: 1, fontSize: '0.72rem', whiteSpace: 'pre-wrap', pt: 0.5,
                              color: a.rationale ? 'text.primary' : 'text.disabled',
                              fontStyle: a.rationale ? 'normal' : 'italic',
                            }}
                          >
                            {a.rationale || 'No reasoning recorded'}
                          </Typography>
                          <Tooltip arrow title="Edit reasoning">
                            <IconButton size="small" aria-label="Edit reasoning"
                              onClick={() => startEdit(it.queue_id)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </TableCell>
                    <TableCell>
                      <ToggleButtonGroup
                        size="small" exclusive value={a.verdict || null}
                        onChange={(_, v) => v && update(it.queue_id, { verdict: v })}
                        orientation="vertical" sx={{ width: '100%' }}
                      >
                        {VERDICTS.map((v) => (
                          <ToggleButton key={v.key} value={v.key} color={v.colour}
                            sx={{ textTransform: 'none', justifyContent: 'flex-start', py: 0.4 }}>
                            <Tooltip arrow title={v.help}>
                              <span style={{ fontSize: '0.75rem' }}>{v.label}</span>
                            </Tooltip>
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip arrow title="Show the device configuration this finding is judged against">
                        <IconButton size="small" onClick={() => setExpanded(open ? null : it.queue_id)}>
                          <CodeIcon fontSize="small" color={open ? 'primary' : 'inherit'} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0, borderBottom: open ? undefined : 'none' }}>
                      <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 1.5, bgcolor: '#263238' }}>
                          <Typography variant="caption" sx={{ color: '#b0bec5', display: 'block', mb: 0.5 }}>
                            {it.device_name} — configuration as audited
                          </Typography>
                          <Box component="pre" sx={{
                            m: 0, maxHeight: 420, overflow: 'auto', color: '#eceff1',
                            fontSize: '0.7rem', fontFamily: 'monospace', whiteSpace: 'pre-wrap',
                          }}>
                            {it.configuration || '(no configuration stored for this device)'}
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast && <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</Alert>}
      </Snackbar>
    </Box>
  );
}
